// ============================================================================
// CHANGO V5.0 — Workspace Settings (boss-only)
// ============================================================================
// Companion for T-D1-028 (PATCH /api/workspace/:id) plus the member invite
// and member-patch endpoints. Boss is the only role that reaches this page
// (RoleGate in main.jsx + backend roleGuard(['boss'])).
//
// Capabilities:
//   - Edit workspace name / brand_display_name / ui_mode / approval_channel
//     / approval_required (toggle).
//   - If approval_required is being toggled false AND the server responds
//     with pending_quotes_count > 0 we surface it (§20 坑 #20).
//   - Invite finance/sales members by email (server enforces FREE_SEAT_QUOTA).
//   - Toggle a finance member's self-approve flag.
//   - Disable (soft-remove) a member.
//
// What is NOT here: billing + seat upgrade UI (D2).
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAuth } from '../../hooks/useAuth.js';
import { api, HttpError } from '../../api/client.js';
import CompanyNameChangeModal from '../../components/CompanyNameChangeModal.jsx';

const APPROVAL_CHANNELS = [
  { value: 'in_app', label: '站内消息' },
  { value: 'wechat', label: '企业微信' },
  { value: 'dingtalk', label: '钉钉' },
  { value: 'feishu', label: '飞书' },
  { value: 'email', label: '邮件' }
];

const UI_MODES = [
  { value: 'simple', label: '精简模式（sales 友好）' },
  { value: 'full', label: '完整模式' }
];

const wsSchema = z.object({
  name: z.string().min(1, '名称必填').max(100, '名称过长'),
  brand_display_name: z.string().max(200, '品牌名过长').optional().or(z.literal('')),
  approval_required: z.boolean(),
  approval_channel: z.enum(['in_app', 'wechat', 'dingtalk', 'feishu', 'email']),
  ui_mode: z.enum(['simple', 'full'])
});

const inviteSchema = z.object({
  email: z.string().email('邮箱格式不正确'),
  role: z.enum(['finance', 'sales'])
});

function Banner({ kind = 'info', children }) {
  const palette = {
    info:    'border-slate-200 bg-slate-50 text-slate-700',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    warn:    'border-amber-200 bg-amber-50 text-amber-800',
    error:   'border-red-200 bg-red-50 text-red-700'
  }[kind] || 'border-slate-200 bg-slate-50 text-slate-700';
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${palette}`} role="status">
      {children}
    </div>
  );
}

function MemberRow({ m, onToggleSelfApprove, onDisable, busy }) {
  const memberLabel = m.display_name || m.contact_name || m.email || `用户 #${m.user_id}`;
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-2 pr-4">
        <div className="text-sm font-medium text-slate-800">{memberLabel}</div>
        <div className="text-xs text-slate-500">{m.email || `用户 #${m.user_id}`}</div>
      </td>
      <td className="py-2 pr-4 text-xs font-mono text-slate-600">{m.role}</td>
      <td className="py-2 pr-4 text-xs text-slate-500">{m.seat_type}</td>
      <td className="py-2 pr-4 text-xs">
        {m.role === 'finance' ? (
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={m.finance_can_self_approve}
              disabled={busy}
              onChange={(e) => onToggleSelfApprove(m.id, e.target.checked)}
            />
            <span>允许自审批</span>
          </label>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="py-2 pr-4 text-xs">
        <span className={m.status === 'active' ? 'text-emerald-700' : 'text-slate-400'}>
          {m.status}
        </span>
      </td>
      <td className="py-2 text-right text-xs">
        {m.status === 'active' && m.role !== 'boss' ? (
          <button
            type="button"
            className="text-red-600 hover:underline disabled:text-slate-400"
            disabled={busy}
            onClick={() => onDisable(m.id)}
          >
            停用
          </button>
        ) : null}
      </td>
    </tr>
  );
}

export default function WorkspaceSettings() {
  const { user, workspace, refresh } = useAuth();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveBanner, setSaveBanner] = useState(null);
  const [inviteBanner, setInviteBanner] = useState(null);
  const [memberBusy, setMemberBusy] = useState(false);
  const [pendingWarn, setPendingWarn] = useState(null);
  const [companyChangeModalOpen, setCompanyChangeModalOpen] = useState(false);
  const [changeHistory, setChangeHistory] = useState([]);
  const [nextEligibleAt, setNextEligibleAt] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoBanner, setLogoBanner] = useState(null);

  const wsId = workspace?.id;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset
  } = useForm({
    resolver: zodResolver(wsSchema),
    defaultValues: {
      name: '',
      brand_display_name: '',
      approval_required: true,
      approval_channel: 'in_app',
      ui_mode: 'simple'
    }
  });

  const {
    register: regInvite,
    handleSubmit: submitInvite,
    formState: { errors: inviteErrors, isSubmitting: inviting },
    reset: resetInvite
  } = useForm({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'sales' }
  });

  // Load workspace + members on mount / wsId change.
  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!wsId) return;
      setLoading(true);
      try {
        const [wsRes, changesRes] = await Promise.all([
          api.get(`/api/workspace/${wsId}`),
          api.get(`/api/workspace/company-name/changes`).catch(() => ({ changes: [], next_eligible_at: null }))
        ]);

        if (ignore) return;
        reset({
          name: wsRes.workspace?.name || '',
          brand_display_name: wsRes.workspace?.brand_display_name || '',
          approval_required: Boolean(wsRes.workspace?.approval_required),
          approval_channel: wsRes.workspace?.approval_channel || 'in_app',
          ui_mode: wsRes.workspace?.ui_mode || 'simple'
        });
        setMembers(Array.isArray(wsRes.members) ? wsRes.members : []);
        setChangeHistory(Array.isArray(changesRes.changes) ? changesRes.changes : []);
        setNextEligibleAt(changesRes.next_eligible_at || null);
      } catch (err) {
        if (!ignore) {
          setSaveBanner({
            kind: 'error',
            text: err instanceof HttpError ? err.message : '加载失败，请刷新重试。'
          });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [wsId, reset]);

  const onSaveSettings = async (values) => {
    setSaveBanner(null);
    setPendingWarn(null);
    try {
      const payload = {
        name: values.name,
        brand_display_name: values.brand_display_name || '',
        approval_required: values.approval_required,
        approval_channel: values.approval_channel,
        ui_mode: values.ui_mode
      };
      const res = await api.patch(`/api/workspace/${wsId}`, payload);
      setSaveBanner({ kind: 'success', text: '已保存。' });
      if (typeof res?.pending_quotes_count === 'number' && res.pending_quotes_count > 0) {
        setPendingWarn(
          `关闭审批流程时仍有 ${res.pending_quotes_count} 条待审批报价；请在列表页逐一处理。`
        );
      }
      // Refresh AuthContext so the header reflects the new workspace name.
      await refresh();
    } catch (err) {
      setSaveBanner({
        kind: 'error',
        text: err instanceof HttpError ? err.message : '保存失败。'
      });
    }
  };

  const onInvite = async (values) => {
    setInviteBanner(null);
    try {
      await api.post(`/api/workspace/${wsId}/members`, values);
      setInviteBanner({ kind: 'success', text: `已邀请 ${values.email}。` });
      resetInvite({ email: '', role: 'sales' });
      // Reload members list.
      const res = await api.get(`/api/workspace/${wsId}`);
      setMembers(Array.isArray(res.members) ? res.members : []);
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.code === 'USER_NOT_FOUND') {
          setInviteBanner({
            kind: 'warn',
            text: '对方尚未注册 CHANGO 账号。请先让其注册后再邀请。'
          });
        } else if (err.code === 'FREE_SEAT_EXHAUSTED') {
          setInviteBanner({
            kind: 'warn',
            text: '该角色的免费席位已用完，请升级套餐或购买额外席位。'
          });
        } else if (err.code === 'ALREADY_MEMBER') {
          setInviteBanner({ kind: 'warn', text: '该成员已在工作区内。' });
        } else {
          setInviteBanner({ kind: 'error', text: err.message });
        }
      } else {
        setInviteBanner({ kind: 'error', text: '邀请失败，请重试。' });
      }
    }
  };

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    setLogoBanner(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.upload('/api/upload/logo', fd);
      await refresh();
      setLogoBanner({ kind: 'success', text: 'Logo 已更新。' });
    } catch (err) {
      setLogoBanner({
        kind: 'error',
        text: err instanceof HttpError ? err.message : 'Logo 上传失败。'
      });
    } finally {
      setLogoBusy(false);
    }
  };

  const onLogoClear = async () => {
    setLogoBusy(true);
    setLogoBanner(null);
    try {
      await api.delete('/api/upload/logo');
      await refresh();
      setLogoBanner({ kind: 'success', text: 'Logo 已清空。' });
    } catch (err) {
      setLogoBanner({
        kind: 'error',
        text: err instanceof HttpError ? err.message : 'Logo 清空失败。'
      });
    } finally {
      setLogoBusy(false);
    }
  };

  const patchMember = async (mid, patch) => {
    setMemberBusy(true);
    try {
      await api.patch(`/api/workspace/${wsId}/members/${mid}`, patch);
      const res = await api.get(`/api/workspace/${wsId}`);
      setMembers(Array.isArray(res.members) ? res.members : []);
    } catch (err) {
      setSaveBanner({
        kind: 'error',
        text: err instanceof HttpError ? err.message : '成员更新失败。'
      });
    } finally {
      setMemberBusy(false);
    }
  };

  const onToggleSelfApprove = (mid, checked) =>
    patchMember(mid, { finance_can_self_approve: checked });

  const onDisable = (mid) => {
    if (typeof window !== 'undefined' && !window.confirm('确定要停用该成员？')) return;
    patchMember(mid, { status: 'disabled' });
  };

  const activeSalesCount = useMemo(
    () => members.filter((m) => m.role === 'sales' && m.status === 'active').length,
    [members]
  );
  const activeFinanceCount = useMemo(
    () => members.filter((m) => m.role === 'finance' && m.status === 'active').length,
    [members]
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">工作区设置</h1>
            <p className="text-xs text-slate-500">仅 boss 可见 · {workspace?.name}</p>
          </div>
          <Link to="/app/dashboard" className="btn-ghost text-sm">返回首页</Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 space-y-8">
        {loading ? (
          <div className="card p-6">加载中…</div>
        ) : (
          <>
            {/* ---- General settings ---- */}
            <section className="card p-6">
              <h2 className="mb-4 text-base font-semibold text-slate-800">基础信息</h2>
              <form onSubmit={handleSubmit(onSaveSettings)} className="space-y-4">
                <div>
                  <label className="label" htmlFor="ws-name">工作区名称</label>
                  <input id="ws-name" className="field" {...register('name')} />
                  {errors.name && <p className="field-error">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="label" htmlFor="ws-brand">对外品牌名</label>
                  <input id="ws-brand" className="field" {...register('brand_display_name')} />
                  {errors.brand_display_name && (
                    <p className="field-error">{errors.brand_display_name.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="label" htmlFor="ws-ui">UI 模式</label>
                    <select id="ws-ui" className="field" {...register('ui_mode')}>
                      {UI_MODES.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label" htmlFor="ws-channel">审批通道</label>
                    <select id="ws-channel" className="field" {...register('approval_channel')}>
                      {APPROVAL_CHANNELS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" {...register('approval_required')} />
                  <span>开启审批流程（推荐）</span>
                </label>

                {saveBanner && <Banner kind={saveBanner.kind}>{saveBanner.text}</Banner>}
                {pendingWarn && <Banner kind="warn">{pendingWarn}</Banner>}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!isDirty || isSubmitting}
                  >
                    {isSubmitting ? '保存中…' : '保存'}
                  </button>
                </div>
              </form>
            </section>

            <section className="card p-6">
              <h2 className="mb-4 text-base font-semibold text-slate-800">对外 Logo</h2>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  {workspace?.company_logo_url ? (
                    <img
                      src={workspace.company_logo_url}
                      alt={workspace?.brand_display_name || workspace?.name || 'CHANGO'}
                      className="h-16 max-w-[220px] rounded border border-slate-200 object-contain p-2"
                    />
                  ) : (
                    <div className="flex h-16 w-40 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
                      未上传
                    </div>
                  )}
                  <div className="text-sm text-slate-600">
                    <p>用于报价单 PDF 和公开分享页抬头。</p>
                    <p className="mt-1 text-xs text-slate-400">支持 PNG/JPG/SVG/WEBP，最大 2MB。</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className="btn-primary cursor-pointer text-sm">
                    {logoBusy ? '处理中…' : '上传 Logo'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="sr-only"
                      disabled={logoBusy}
                      onChange={onLogoUpload}
                    />
                  </label>
                  {workspace?.company_logo_url ? (
                    <button type="button" className="btn-ghost text-sm" disabled={logoBusy} onClick={onLogoClear}>
                      清空
                    </button>
                  ) : null}
                </div>
              </div>
              {logoBanner && (
                <div className="mt-4">
                  <Banner kind={logoBanner.kind}>{logoBanner.text}</Banner>
                </div>
              )}
            </section>

            {/* ---- Members ---- */}
            <section className="card p-6">
              <div className="mb-4 flex items-end justify-between">
                <h2 className="text-base font-semibold text-slate-800">成员</h2>
                <p className="text-xs text-slate-500">
                  当前 finance 活跃 {activeFinanceCount} 人 · sales 活跃 {activeSalesCount} 人
                </p>
              </div>

              <form onSubmit={submitInvite(onInvite)} className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px_auto]">
                <div>
                  <input
                    className="field"
                    placeholder="邀请邮箱"
                    {...regInvite('email')}
                  />
                  {inviteErrors.email && (
                    <p className="field-error">{inviteErrors.email.message}</p>
                  )}
                </div>
                <div>
                  <select className="field" {...regInvite('role')}>
                    <option value="sales">sales</option>
                    <option value="finance">finance</option>
                  </select>
                </div>
                <button type="submit" className="btn-primary" disabled={inviting}>
                  {inviting ? '邀请中…' : '邀请'}
                </button>
              </form>

              {inviteBanner && (
                <div className="mb-4">
                  <Banner kind={inviteBanner.kind}>{inviteBanner.text}</Banner>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                      <th className="py-2 pr-4">成员</th>
                      <th className="py-2 pr-4">角色</th>
                      <th className="py-2 pr-4">席位</th>
                      <th className="py-2 pr-4">选项</th>
                      <th className="py-2 pr-4">状态</th>
                      <th className="py-2 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-sm text-slate-400">
                          暂无成员
                        </td>
                      </tr>
                    ) : (
                      members.map((m) => (
                        <MemberRow
                          key={m.id}
                          m={m}
                          onToggleSelfApprove={onToggleSelfApprove}
                          onDisable={onDisable}
                          busy={memberBusy}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <p className="text-center text-xs text-slate-400">
              需要升级套餐或管理计费？<button
                type="button"
                className="text-chango-700 hover:underline"
                onClick={() => navigate('/app/billing/plans')}
              >前往计费页面</button>（D2 上线）
            </p>

            {/* ---- Company Name Change (§9.6.2e) ---- */}
            <section className="card border-l-4 border-l-amber-500 p-6">
              <div className="mb-4">
                <h2 className="text-base font-semibold text-slate-800">公司全称变更</h2>
                <p className="mt-2 text-xs text-slate-600">
                  修改营业执照对应的公司全称需要上传新执照和短信验证。§9.6.2e 反作弊规则：30 天内最多申请一次。
                </p>
              </div>

              <div className="space-y-3">
                <div className="rounded bg-slate-50 p-3">
                  <p className="text-sm font-medium text-slate-800">当前公司全称</p>
                  <p className="mt-1 text-base font-mono text-slate-700">{workspace?.company_name || '—'}</p>
                </div>

                {nextEligibleAt && (
                  <div className="rounded border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs text-amber-700">
                      <strong>30 天冷却中：</strong> 请于 {new Date(nextEligibleAt).toLocaleDateString('zh-CN')} 后重新申请
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setCompanyChangeModalOpen(true)}
                  disabled={!!nextEligibleAt}
                >
                  申请变更公司全称
                </button>

                {changeHistory && changeHistory.length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
                    <p className="text-xs font-semibold text-slate-600">变更历史</p>
                    <div className="space-y-1 text-xs">
                      {changeHistory.map((ch) => (
                        <div key={ch.id} className="flex items-start gap-2 rounded bg-slate-50 p-2">
                          <span className="text-slate-600">{new Date(ch.created_at).toLocaleDateString('zh-CN')}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                            ch.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            ch.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {ch.status === 'approved' ? '已通过' : ch.status === 'rejected' ? '已拒绝' : '待审核'}
                          </span>
                          <span className="text-slate-700">{ch.requested_company_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ---- Data Export (Task #114) ---- */}
            <section className="card border-l-4 border-l-chango-500 p-6">
              <div className="mb-4">
                <h2 className="text-base font-semibold text-slate-800">打包下载全部数据</h2>
                <p className="mt-2 text-xs text-slate-600">
                  包含所有客户、产品、报价单（PDF + Excel）、历史事件、成员列表。仅 boss 可操作。
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">用途：</span> 合规数据导出（GDPR）+ 客户 cutover 安心感
                </p>

                <div className="rounded bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                  <p>打包通常需要 30~60 秒，请耐心等待。</p>
                  <p>每个 workspace 每小时最多导出 1 次（防资源滥用）。</p>
                  <p>导出时包含 PDF 渲染 · 若单张报价单失败不会中断整包，会生成 .ERROR.txt 说明。</p>
                </div>

                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    window.open(`/api/workspace/${workspace?.id}/export-all`, '_blank');
                  }}
                >
                  ⬇️ 下载数据包 (.zip)
                </button>
              </div>
            </section>
          </>
        )}

        <CompanyNameChangeModal
          isOpen={companyChangeModalOpen}
          onClose={() => setCompanyChangeModalOpen(false)}
          currentName={workspace?.company_name}
          phone={user?.phone || ''}
          onSuccess={() => {
            setCompanyChangeModalOpen(false);
            // Reload history
            api.get(`/api/workspace/company-name/changes`).then(res => {
              setChangeHistory(Array.isArray(res.changes) ? res.changes : []);
              setNextEligibleAt(res.next_eligible_at || null);
            }).catch(() => {});
          }}
        />
      </main>
    </div>
  );
}
